import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import ReportDTO from "../external/ensolvers-core-frontend-mui/dto/ReportDTO";
import ApiError from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiError";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import useLocalStorage, { removeItem, STORAGE_KEYS } from "../hooks/useLocalStorage";
import Assignation from "../models/Assignation";
import Position from "../models/Position";
import WorkLog from "../models/WorkLog";
import WorkLogReport from "../models/WorkLogReport";
import { BatchWorkLogWeekly } from "../models/WorkLogWeekly";
import ProjectService from "../services/ProjectService";
import { QueryKeyFactory } from "../services/queryKeys";
import WorkLogService from "../services/WorkLogService";
import WorkLogWeeklyService from "../services/WorkLogWeeklyService";
import { RequestStatus } from "../types/RequestStatus";
import { Routes } from "../types/Routes";
import OpsDateUtils from "../utils/OpsDateUtils";
import { unwrap } from "../utils/queryFn";
import RoleUtils from "../utils/RoleUtils";
import { TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";
import useUpdateUrl from "./useUpdateUrl";
import { useStoredDate } from "./useStoredDate";

export const WORKDAYS_HEADER_TYPE = "workdays";

interface WorkDaysPerCountry {
  country: string;
  count: number;
}
interface WorkdaysPerWeek {
  week: number;
  workdays: WorkDaysPerCountry[];
}

interface WorkdaysResponse {
  [key: string]: {
    [key: string]: number;
  };
}

const projectKeys = new QueryKeyFactory(ProjectService.path);
const workLogKeys = new QueryKeyFactory(WorkLogService.path);
const REPORT_CONTEXT = { scope: "weekly-report" };
const NO_NAMES: AutoCompleteUIDTO[] = [];
const NO_ROWS: WorkLogReport[] = [];

function getQueryStatus(query: { isError: boolean; isFetching: boolean; isSuccess: boolean }): RequestStatus | undefined {
  if (query.isError) return RequestStatus.ERROR;
  if (query.isFetching) return RequestStatus.LOADING;
  if (query.isSuccess) return RequestStatus.SUCCESS;
  return undefined;
}

function indexRows(body: WorkLogReport[]): WorkLogReport[] {
  return body.map((row, index) => ({ ...row, index }));
}

const useWorklogReportList = () => {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useStoredDate();
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);
  const [search, setSearch] = useState<AutoCompleteUIDTO>(storedProject);
  const [reportToAdjust, setReportToAdjust] = useState<WorkLogReport | undefined>(undefined);
  const [manualLoading, setManualLoading] = useState(false);
  const [onlyExternal, setOnlyExternal] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ONLY_EXTERNAL, false);
  const [assigned, setAssigned] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ASSIGNED_TO_ME, false);
  const [projectsAssigned] = useState<AutoCompleteUIDTO[]>([]);
  const searchParams = new URLSearchParams(useLocation().search);
  const project = searchParams.get("project");
  const monthParam = searchParams.get("month");
  const yearParam = searchParams.get("year");
  const assignedToMe = searchParams.get("assigned");
  const onlyExternalParam = searchParams.get("onlyExternal");
  const [selectedReport, setSelectedReport] = useState<WorkLogReport | null>();
  const { updateUrl } = useUpdateUrl(Routes.WORKLOG_REPORT, searchParams);
  const [adjustFixed, setAdjustFixed] = useState<boolean>(false);
  const [batchReportsToAdjust, setBatchReportsToAdjust] = useState<WorkLogReport[] | undefined>(undefined);
  const [batchReportsToApprove, setBatchReportsToApprove] = useState<WorkLogReport[] | undefined>(undefined);

  const projectExternalId = search?.externalId || "";
  const year = selectedDate ? selectedDate.getFullYear() : new Date().getFullYear();
  const month = selectedDate ? selectedDate.getMonth() : new Date().getMonth();

  const projectsQuery = useQuery({
    queryKey: projectKeys.list({ scope: "worklog-report-names", onlyExternal, assigned }),
    queryFn: () => unwrap(ProjectService.findAllProjectsNames(onlyExternal, assigned)),
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const projectNames = projectsQuery.data ?? NO_NAMES;

  const reportQueryKey = workLogKeys.list({ projectExternalId, year, month, assigned, onlyExternal }, REPORT_CONTEXT);
  const reportQuery = useQuery({
    queryKey: reportQueryKey,
    queryFn: () => unwrap(WorkLogService.getWorkLogsPerWeek(projectExternalId, year, month)),
    enabled: !!projectExternalId,
  });

  const managerQuery = useQuery({
    queryKey: projectKeys.detail(`manager:${projectExternalId}`),
    queryFn: async () => {
      try {
        await unwrap(ProjectService.isUserCoreManagerFromProject(projectExternalId, AuthService.getUser()?.externalId!));
        return true;
      } catch {
        return false;
      }
    },
    enabled: !!projectExternalId && !RoleUtils.isAdmin(),
  });
  const canEdit = RoleUtils.isAdmin() || !!managerQuery.data;

  const refreshReport = () => queryClient.invalidateQueries({ queryKey: workLogKeys.lists(REPORT_CONTEXT) });

  const headers = reportQuery.data?.headers;
  const rows = useMemo(() => indexRows(reportQuery.data?.body ?? NO_ROWS), [reportQuery.data]);
  const workdays = useMemo(() => transformIntoWorkdays(reportQuery.data?.additionalInformation), [reportQuery.data]);
  const weeks = useMemo(
    () => (headers ?? []).filter((header) => header.type === WORKDAYS_HEADER_TYPE).map((header) => header.label),
    [headers],
  );

  const setRows: Dispatch<SetStateAction<WorkLogReport[]>> = (action) => {
    queryClient.setQueryData<ReportDTO<WorkLogReport>>(reportQueryKey, (current) => {
      if (!current) return current;
      const prev = indexRows(current.body);
      const next = typeof action === "function" ? action(prev) : action;
      return { ...current, body: next };
    });
  };

  const requestStatus = getQueryStatus(reportQuery);

  const adjustMutation = useMutation({
    mutationFn: (report: WorkLogReport) =>
      unwrap(WorkLogService.adjustHours(report.assignationExternalId, report.coreUserExternalId, year, month, report)),
    onSuccess: async (message) => {
      NotificationService.info(message);
      await refreshReport();
    },
  });

  const batchAdjustMutation = useMutation({
    mutationFn: async (reports: WorkLogReport[]) => {
      for (const report of reports) {
        if (allWeeksApproved(report)) {
          NotificationService.error("You can't adjust hours with all the weeks approved");
          continue;
        }
        try {
          const message = await unwrap(
            WorkLogService.adjustHours(report.assignationExternalId, report.coreUserExternalId, year, month, report),
          );
          NotificationService.info(message);
        } catch (error) {
          if (error instanceof ApiError) error.notifyError();
          else throw error;
        }
      }
    },
    onSuccess: () => refreshReport(),
  });

  const approveMutation = useMutation({
    mutationFn: (payload: BatchWorkLogWeekly) => unwrap(WorkLogWeeklyService.batchApproveWeeks(payload)),
    onSuccess: () => refreshReport(),
  });

  useEffect(() => {
    if (!reportQuery.isFetching && !projectsQuery.isFetching) setManualLoading(false);
  }, [reportQuery.isFetching, projectsQuery.isFetching]);

  useEffect(() => {
    if (project === null || projectNames.length === 0) return;
    const projectFilter = projectNames.find((item) => item.externalId === project);
    if (projectFilter) {
      setSearch(projectFilter);
      setStoredProject(projectFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectNames]);

  useEffect(() => {
    const date = new Date();
    if (monthParam !== null) {
      date.setMonth(parseInt(monthParam));
      setSelectedDate(date);
    } else {
      searchParams.set("month", (date.getMonth() - 1).toString());
    }

    if (yearParam !== null) {
      date.setFullYear(parseInt(yearParam));
      setSelectedDate(date);
    } else {
      searchParams.set("year", date.getFullYear().toString());
    }

    if (assignedToMe !== null) {
      setAssigned(assignedToMe === "true");
    } else if (assigned !== undefined) {
      searchParams.set("assigned", assigned.toString());
    }

    if (onlyExternalParam !== null) {
      setOnlyExternal(onlyExternalParam === "true");
    } else if (onlyExternal !== undefined) {
      searchParams.set("onlyExternal", onlyExternal.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsAssigned]);

  const handleOnChangeWorklogReport = () => {
    setAssigned(!assigned);
    setStoredProject(EMPTY_AUTOCOMPLETE);
    setSearch(EMPTY_AUTOCOMPLETE);
    removeItem("STORED_PROJECT");
    searchParams.set("assigned", (!assigned).toString());
    searchParams.delete("project");
    updateUrl();
  };

  const allWeeksApproved = (report: WorkLogReport): boolean => {
    if (headers) {
      const unapprovedWeeks = Object.entries(report.validated as Map<number, boolean>).filter(
        (val) =>
          !val[1] &&
          OpsDateUtils.isAfter(
            OpsDateUtils.addWeeksToDate(new Date(`${selectedDate.getFullYear()}-01-01`), parseInt(val[0])),
            report.positionStartDate!,
          ),
      );

      return unapprovedWeeks.length === 0;
    }
    return false;
  };

  const handleAdjustHours = async () => {
    if (!reportToAdjust) return;
    if (allWeeksApproved(reportToAdjust)) {
      NotificationService.error("You can't adjust hours with all the weeks approved");
      return;
    }
    try {
      await adjustMutation.mutateAsync(reportToAdjust);
    } catch {
      // QueryClient already toasts ApiError from the failed adjust.
    } finally {
      setReportToAdjust(undefined);
    }
  };

  const handleBatchAdjustHours = async () => {
    try {
      await batchAdjustMutation.mutateAsync(batchReportsToAdjust || []);
    } finally {
      setBatchReportsToAdjust(undefined);
    }
  };

  const approveWeeks = (payload: BatchWorkLogWeekly) => approveMutation.mutateAsync(payload);

  const handleChangeExternalProjects = () => {
    setOnlyExternal(!onlyExternal);
    searchParams.set("onlyExternal", (!onlyExternal).toString());
    searchParams.delete("project");
    setStoredProject(EMPTY_AUTOCOMPLETE);
    updateUrl();
  };

  const clearWorklogReport = () => setReportToAdjust(undefined);
  const clearRequestStatus = () => undefined;
  const clearBatchReport = () => setBatchReportsToAdjust(undefined);

  const findMinDayOfCurrentWeek = () => {
    const currentDate = new Date();
    const currentDay = currentDate.getDate();

    let intervalIndex = -1;

    for (let i = 0; i < weeks.length; i++) {
      const [start, end] = weeks[i].split(" - ");
      if (currentDay >= Number(start) && currentDay <= Number(end)) {
        intervalIndex = i;
        break;
      }
    }
    if (intervalIndex === -1) {
      return null;
    }

    const [start] = weeks[intervalIndex].split(" - ");
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    return new Date(currentYear, currentMonth, Number(start));
  };

  const onWorkLogUpdate = async (workLog: WorkLog, prevHours: number) => {
    if (!selectedReport) {
      return;
    }
    const day: Date = new Date(workLog.shortDay);
    const weekNumber: number = OpsDateUtils.getWeekOfYear(day);
    const { hours, index } = selectedReport;
    hours[weekNumber] = hours[weekNumber] + (workLog.hours - prevHours);

    const updatedRows: WorkLogReport[] = rows.map((row) => (row.index === index ? { ...row, hours: hours } : row));
    setRows(updatedRows);
  };

  const setPositionUpdated = (_position?: Position) => {
    void refreshReport();
  };

  const setAssignationUpdated = (_assignation?: Assignation) => {
    void refreshReport();
  };

  return {
    worklogReportState: {
      reportToAdjust,
      setReportToAdjust,
      clearWorklogReport,
      handleAdjustHours,
      allWeeksApproved,
      setAdjustFixed,
      adjustFixed,
      setBatchReportsToAdjust,
      setBatchReportsToApprove,
      batchReportsToApprove,
      batchReportsToAdjust,
      clearBatchReport,
      handleBatchAdjustHours,
      approveWeeks,
      setPositionUpdated,
      setAssignationUpdated,
    },
    requestStatus,
    clearRequestStatus,
    handleChangeExternalProjects,
    headers,
    rows,
    setRows,
    selectedDate,
    setSelectedDate,
    isLoading: manualLoading || projectsQuery.isFetching || reportQuery.isFetching || approveMutation.isPending,
    setIsLoading: setManualLoading,
    canEdit,
    search,
    setSearch,
    projectNames,
    setStoredProject,
    onlyExternal,
    handleOnChangeWorklogReport,
    setAssigned,
    assigned,
    projectsAssigned,
    workdays,
    storedProject,
    weeks,
    findMinDayOfCurrentWeek,
    selectedReport,
    setSelectedReport,
    onWorkLogUpdate,
  };
};

function transformIntoWorkdays(additionalInfo?: WorkdaysResponse): WorkdaysPerWeek[] | undefined {
  if (!additionalInfo) return undefined;
  return Object.entries(additionalInfo).map(([week, countries]) => ({
    week: parseInt(week),
    workdays: Object.entries(countries).map(([country, count]) => ({ country, count })),
  }));
}

export default useWorklogReportList;
